/**
 * A grade de cartões de módulo, de até quatro colunas.
 *
 * Papel: este é o `.card-grid` — gap de 20px, no máximo 1140px de largura —,
 * usado pelos cartões da tela inicial, que quebram para duas colunas em 920px e
 * para uma em 560px, e pelos dois do submenu da Fazenda Boa Vista dentro de
 * Residências, que ficam em quatro colunas em qualquer largura.
 *
 * Como usar: quem chama entrega os `<li>` já prontos, um por cartão, porque é o
 * conteúdo de cada item — não a grade — que muda entre a tela inicial e o
 * submenu. A grade sempre declara as quatro colunas, mesmo com menos itens: com
 * dois cartões, as duas colunas finais ficam vazias em vez de os dois cartões
 * se esticarem para preenchê-las. `centralizada` distingue a tela inicial, que
 * centra a própria grade dentro da tela toda, do submenu, que a mantém
 * encostada à esquerda do bloco em que nasce.
 *
 * `colunasFixas` reproduz o estilo em linha que a origem escreve no submenu
 * (`grid-template-columns:repeat(4,1fr)`), que vence as duas media queries do
 * `.card-grid`. É `1fr`, e não `grid-cols-4`: o Tailwind escreve
 * `minmax(0,1fr)`, e a coluna encolheria abaixo do nome do cartão, deixando o
 * nome vazar pela borda. Com `1fr`, abaixo de uns 460px de janela a grade passa
 * da largura do `<main>` e a página ganha rolagem lateral, como na origem.
 */
import { type ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';

interface GradeDeCartoesProps {
  /** Os `<li>` da grade, um por cartão. */
  children: ReactNode;
  /** Centra a grade dentro do próprio contêiner; sem ela, a grade nasce à esquerda. */
  centralizada?: boolean;
  /** Quatro colunas em qualquer largura, sem as quebras de 920px e 560px. */
  colunasFixas?: boolean;
}

/**
 * A grade de cartões de módulo.
 *
 * @param children - Os `<li>` da grade, um por cartão.
 * @param centralizada - Quando verdadeiro, aplica `mx-auto` — o caso da grade da
 *   tela inicial, que ocupa a tela sozinha.
 * @param colunasFixas - Quando verdadeiro, troca as três classes de coluna por
 *   `repeat(4,1fr)` — o caso do submenu da Fazenda Boa Vista.
 * @returns A `<ul>` da grade, pronta para receber os `<li>` de quem chama.
 */
export function GradeDeCartoes({
  children,
  centralizada = false,
  colunasFixas = false,
}: GradeDeCartoesProps) {
  return (
    <ul
      className={cn(
        'grid max-w-[1140px] gap-5',
        colunasFixas
          ? '[grid-template-columns:repeat(4,1fr)]'
          : 'grid-cols-1 min-[561px]:grid-cols-2 min-[921px]:grid-cols-4',
        centralizada && 'mx-auto',
      )}
    >
      {children}
    </ul>
  );
}
